<template>
  <div id="consultasView">
    <AppBar />

    <div class="area">
      <VerificationPendingBanner />

      <div class="area-head">
        <div>
          <h1 class="greet" style="font-size:var(--font-size-3xl)">
            Minhas consultas
          </h1>
          <p class="sub">Histórico completo de teleconsultas e agendamentos.</p>
        </div>
        <router-link
          :to="{ path: '/', query: { buscar: '1' } }"
          class="btn btn--accent btn--lg"
        >
          Agendar consulta
        </router-link>
      </div>

      <div class="tabs">
        <span
          v-for="tab in tabs"
          :key="tab.key"
          class="tab"
          :class="{ 'tab--active': activeTab === tab.key }"
          style="cursor:pointer"
          @click="activeTab = tab.key"
        >
          {{ tab.label }} · {{ tab.count }}
        </span>
      </div>

      <!-- PRÓXIMAS -->
      <div v-show="activeTab === 'proximas'">
        <div v-if="loading" class="state-box">
          <p>Carregando...</p>
        </div>
        <div v-else-if="futureAppointments.length" class="appt-list">
          <div
            v-for="appt in futureAppointments"
            :key="appt.id"
            class="card appt-row"
            :class="{ 'card--success': !!appt.room }"
          >
            <div class="when">
              <div class="d">{{ formatDay(appt.scheduled_at) }}</div>
              <div class="h">{{ formatTime(appt.scheduled_at) }}</div>
            </div>
            <div class="appt-info">
              <div class="row" style="gap:var(--space-3)">
                <div
                  class="avatar avatar--sm"
                  :class="{ 'avatar--ring': !!appt.room }"
                >
                  <img
                    v-if="appt.doctor && appt.doctor.photo"
                    :src="appt.doctor.photo"
                    :alt="appt.doctor.name"
                    loading="lazy"
                    decoding="async"
                  />
                  <template v-else>{{ doctorInitials(appt) }}</template>
                </div>
                <span class="nm">{{ appt.doctor && appt.doctor.name }}</span>
                <span
                  v-if="appt.order && appt.order.status === 'pending'"
                  class="badge badge--warning"
                >
                  Aguardando pagamento
                </span>
                <span v-else-if="appt.room" class="badge badge--success">
                  Confirmada · {{ apptCountdown(appt) }}
                </span>
                <span v-else class="badge badge--info">Agendada</span>
              </div>
              <span class="meta">{{ apptMeta(appt) }}</span>
            </div>
            <div class="appt-acts">
              <router-link
                v-if="appt.room"
                :to="'/sala-de-espera/' + appt.room.id"
                class="btn btn--accent btn--sm"
              >
                Entrar na sala
              </router-link>
              <button
                v-if="canReschedule(appt)"
                class="btn btn--ghost btn--sm"
                :disabled="reschedulingId === appt.id"
                @click="openReschedule(appt)"
              >
                {{ reschedulingId === appt.id ? "Remarcando…" : "Remarcar" }}
              </button>
              <button
                v-if="canReschedule(appt)"
                class="btn btn--outline btn--sm btn--danger-outline"
                :disabled="cancelingId === appt.id"
                @click="cancelConsulta(appt)"
              >
                {{
                  cancelingId === appt.id ? "Cancelando…" : "Cancelar consulta"
                }}
              </button>
            </div>
            <div class="appt-exams">
              <ExamUpload :appointment-id="appt.id" />
            </div>
          </div>
        </div>
        <div v-else class="state-box">
          <div class="ill">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
              <rect
                x="3"
                y="4"
                width="18"
                height="17"
                rx="2"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M3 9h18M8 2v4M16 2v4"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
          </div>
          <h3>Nenhuma consulta agendada</h3>
          <p>Quando você agendar uma consulta ela aparecerá aqui.</p>
          <router-link
            :to="{ path: '/', query: { buscar: '1' } }"
            class="btn btn--accent btn--sm"
          >
            Agendar agora
          </router-link>
        </div>
        <list-pagination
          v-if="!loading && futureAppointments.length"
          :page="pages.proximas"
          :total="appointmentsTotals.future"
          label="consultas"
          @change="goToPage('proximas', $event)"
        />
      </div>

      <!-- REALIZADAS -->
      <div v-show="activeTab === 'realizadas'">
        <div v-if="loading" class="state-box"><p>Carregando...</p></div>
        <div v-else-if="pastAppointments.length" class="appt-list">
          <div
            v-for="appt in pastAppointments"
            :key="appt.id"
            class="card appt-row is-past"
          >
            <div class="when">
              <div class="d">{{ formatDay(appt.scheduled_at) }}</div>
              <div class="h">{{ formatTime(appt.scheduled_at) }}</div>
            </div>
            <div class="appt-info">
              <div class="row" style="gap:var(--space-3)">
                <div class="avatar avatar--sm">
                  <img
                    v-if="appt.doctor && appt.doctor.photo"
                    :src="appt.doctor.photo"
                    :alt="appt.doctor.name"
                    loading="lazy"
                    decoding="async"
                  />
                  <template v-else>{{ doctorInitials(appt) }}</template>
                </div>
                <span class="nm">{{ appt.doctor && appt.doctor.name }}</span>
                <span class="badge badge--neutral badge--no-dot">
                  Realizada
                </span>
              </div>
              <span class="meta">{{ apptMeta(appt) }}</span>
            </div>
            <div class="appt-acts">
              <router-link to="/documentos" class="btn btn--outline btn--sm">
                Ver documentos
              </router-link>
              <!-- Avaliação da consulta (Edital INAS, Anexo V, item 3.8) -->
              <router-link
                :to="{ name: 'AvaliarConsulta', params: { id: appt.id } }"
                class="btn btn--accent btn--sm"
              >
                Avaliar consulta
              </router-link>
              <!-- <router-link :to="{ path: '/', query: { buscar: '1' } }" class="btn btn--ghost btn--sm">Agendar retorno</router-link> -->
            </div>
            <div class="appt-exams">
              <ExamUpload :appointment-id="appt.id" />
            </div>
          </div>
        </div>
        <div v-else class="state-box">
          <div class="ill">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M9 12l2 2 4-4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3>Nenhuma consulta realizada ainda</h3>
          <p>Suas consultas concluídas aparecerão aqui.</p>
        </div>
        <list-pagination
          v-if="!loading && pastAppointments.length"
          :page="pages.realizadas"
          :total="appointmentsTotals.past"
          label="consultas"
          @change="goToPage('realizadas', $event)"
        />
      </div>

      <!-- CANCELADAS -->
      <div v-show="activeTab === 'canceladas'">
        <div v-if="loading" class="state-box"><p>Carregando...</p></div>
        <div v-else-if="canceledAppointments.length" class="appt-list">
          <div
            v-for="appt in canceledAppointments"
            :key="appt.id"
            class="card appt-row is-cancel"
          >
            <div class="when">
              <div class="d">{{ formatDay(appt.scheduled_at) }}</div>
              <div class="h">{{ formatTime(appt.scheduled_at) }}</div>
            </div>
            <div class="appt-info">
              <div class="row" style="gap:var(--space-3)">
                <div class="avatar avatar--sm">
                  <img
                    v-if="appt.doctor && appt.doctor.photo"
                    :src="appt.doctor.photo"
                    :alt="appt.doctor.name"
                    loading="lazy"
                    decoding="async"
                  />
                  <template v-else>{{ doctorInitials(appt) }}</template>
                </div>
                <span class="nm">{{ appt.doctor && appt.doctor.name }}</span>
                <span class="badge badge--danger">Cancelada</span>
              </div>
              <span class="meta">{{ apptMeta(appt) }}</span>
            </div>
            <div class="appt-acts">
              <span
                v-if="appt.cancelation && appt.cancelation.refunded_amount"
                class="badge badge--success badge--no-dot"
                style="justify-content:center"
              >
                Reembolso R$
                {{ formatCurrency(appt.cancelation.refunded_amount) }} ✓
              </span>
              <!-- <router-link :to="{ path: '/', query: { buscar: '1' } }" class="btn btn--ghost btn--sm">Reagendar</router-link> -->
            </div>
          </div>
        </div>
        <div v-else class="state-box">
          <div class="ill">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <h3>Nenhuma consulta cancelada</h3>
          <p>
            Consultas que você cancelar aparecem aqui, com o status do
            reembolso.
          </p>
        </div>
        <list-pagination
          v-if="!loading && canceledAppointments.length"
          :page="pages.canceladas"
          :total="appointmentsTotals.canceled"
          label="consultas"
          @change="goToPage('canceladas', $event)"
        />
      </div>

      <!-- REEMBOLSADAS -->
      <div v-show="activeTab === 'reembolsadas'">
        <div v-if="loading" class="state-box"><p>Carregando...</p></div>
        <div v-else-if="refundedAppointments.length" class="appt-list">
          <div
            v-for="appt in refundedAppointments"
            :key="appt.id"
            class="card appt-row is-cancel"
          >
            <div class="when">
              <div class="d">{{ formatDay(appt.scheduled_at) }}</div>
              <div class="h">{{ formatTime(appt.scheduled_at) }}</div>
            </div>
            <div class="appt-info">
              <div class="row" style="gap:var(--space-3)">
                <div class="avatar avatar--sm">
                  <img
                    v-if="appt.doctor && appt.doctor.photo"
                    :src="appt.doctor.photo"
                    :alt="appt.doctor.name"
                    loading="lazy"
                    decoding="async"
                  />
                  <template v-else>{{ doctorInitials(appt) }}</template>
                </div>
                <span class="nm">{{ appt.doctor && appt.doctor.name }}</span>
                <span class="badge badge--danger">Reembolsada</span>
              </div>
              <span class="meta">{{ apptMeta(appt) }}</span>
            </div>
            <div class="appt-acts">
              <span
                v-if="appt.order && appt.order.final_price"
                class="badge badge--success badge--no-dot"
                style="justify-content:center"
              >
                Reembolso R$
                {{ formatCurrency(appt.order.final_price) }} ✓
              </span>
            </div>
          </div>
        </div>
        <div v-else class="state-box">
          <div class="ill">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <h3>Nenhuma consulta reembolsada</h3>
          <p>
            Estornos feitos fora de um cancelamento aparecem aqui.
          </p>
        </div>
        <list-pagination
          v-if="!loading && refundedAppointments.length"
          :page="pages.reembolsadas"
          :total="appointmentsTotals.refunded"
          label="consultas"
          @change="goToPage('reembolsadas', $event)"
        />
      </div>
    </div>

    <!-- Modal Remarcar: horários livres do mesmo médico -->
    <b-modal
      v-if="rescheduleTarget"
      id="reschedule-modal"
      title="Remarcar consulta"
      size="lg"
      hide-footer
      centered
      modal-class="remarcar-consulta-modal"
      @hidden="rescheduleTarget = null"
    >
      <p class="sub">
        Consulta atual: {{ formatDay(rescheduleTarget.scheduled_at) }} às
        {{ formatTime(rescheduleTarget.scheduled_at) }}.
        <br />
        Escolha o novo horário:
      </p>
      <TimeTable
        :doctor="rescheduleTarget.doctor"
        @select="confirmReschedule"
      />
    </b-modal>

    <footer class="foot-mini">
      <router-link to="/" class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </router-link>
      <span class="cpy">© 2026 Videomedi · CNPJ 00.000.000/0001-00</span>
      <div class="links">
        <a href="#">Privacidade</a>
        <a href="#">Termos de Uso</a>
        <a href="#">LGPD</a>
        <a href="#">Suporte</a>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  format,
  parseISO,
  isToday,
  isTomorrow,
  differenceInDays
} from "date-fns";
import { pt as ptLocale } from "date-fns/locale";
import AppBar from "../components/AppBar.vue";
import VerificationPendingBanner from "../components/VerificationPendingBanner.vue";
import ExamUpload from "../components/ExamUpload.vue";
import TimeTable from "../components/TimeTable.vue";
import ListPagination from "../components/ListPagination.vue";
import { formatDate } from "../utils/masks/formatSlotDate";

// Aba da tela → action do store que busca aquela lista.
const TAB_ACTIONS = {
  proximas: "fetchFutureAppointments",
  realizadas: "fetchPastAppointments",
  canceladas: "fetchCanceledAppointments",
  reembolsadas: "fetchRefundedAppointments"
};

export default {
  name: "ConsultasView",
  components: {
    AppBar,
    VerificationPendingBanner,
    ExamUpload,
    TimeTable,
    ListPagination
  },

  data() {
    return {
      activeTab: "proximas",
      // Página atual de cada aba (10 consultas por página, paginado no back).
      pages: { proximas: 1, realizadas: 1, canceladas: 1, reembolsadas: 1 },
      loading: false,
      cancelingId: null,
      reschedulingId: null,
      rescheduleTarget: null
    };
  },

  async mounted() {
    document.body.className = "";
    document.body.classList.add("Base");
    this.loading = true;
    await Promise.all(
      Object.keys(TAB_ACTIONS).map(this.fetchTab)
    ).catch(() => {});
    this.loading = false;
  },

  methods: {
    async fetchTab(tab) {
      const response = await this.$store.dispatch(
        `appointments/${TAB_ACTIONS[tab]}`,
        { page: this.pages[tab] }
      );
      // Página que ficou vazia (ex.: cancelou a última consulta dela) volta uma.
      if (this.pages[tab] > 1 && response?.data?.length === 0) {
        this.pages[tab] -= 1;
        return this.fetchTab(tab);
      }
      return response;
    },
    goToPage(tab, page) {
      this.pages[tab] = page;
      return this.fetchTab(tab);
    },
    formatDay(iso) {
      return format(parseISO(iso), "EEE · d MMM yyyy", { locale: ptLocale });
    },
    formatTime(iso) {
      return format(parseISO(iso), "HH:mm");
    },
    doctorInitials(appt) {
      const name = (appt.doctor && appt.doctor.name) || "";
      return (
        name
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },
    apptCountdown(appt) {
      const date = parseISO(appt.scheduled_at);
      if (isToday(date)) return "hoje";
      if (isTomorrow(date)) return "amanhã";
      const days = differenceInDays(date, new Date());
      return `em ${days} dias`;
    },
    apptMeta(appt) {
      const specialty =
        (appt.doctor &&
          appt.doctor.doctor_property &&
          appt.doctor.doctor_property.medical_specialty_name) ||
        "";
      const dp = appt.doctor && appt.doctor.doctor_property;
      const crm =
        dp && dp.council_number
          ? `${dp.council_name || "CRM"} ${dp.council_number}${
              dp.council_uf ? " " + dp.council_uf : ""
            }`
          : null;
      const duration =
        appt.scheduled_at && appt.scheduled_until
          ? Math.round(
              (new Date(appt.scheduled_until) - new Date(appt.scheduled_at)) /
                60000
            )
          : null;
      return [specialty, crm, "por vídeo", duration ? `${duration} min` : null]
        .filter(Boolean)
        .join(" · ");
    },
    formatCurrency(cents) {
      return (cents / 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 0
      });
    },
    async cancelConsulta(appt) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Deseja cancelar esta consulta?",
        {
          title: "Cancelar consulta",
          okTitle: "Sim, cancelar",
          cancelTitle: "Voltar",
          okVariant: "cancel-confirm",
          cancelVariant: "cancel-outline",
          modalClass: "cancel-consulta-modal"
        }
      );
      if (!confirmed) return;

      this.cancelingId = appt.id;
      try {
        await this.$store.dispatch("appointments/cancelAppointment", {
          appointment_id: appt.id,
          note: ""
        });
        await Promise.all([
          this.fetchTab("proximas"),
          this.fetchTab("canceladas")
        ]);
      } catch (error) {
        const message =
          error?.response?.data?.errors?.details ||
          "Não foi possível cancelar a consulta. Tente novamente.";
        this.$bvToast.toast(message, {
          title: "Erro",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000
        });
      } finally {
        this.cancelingId = null;
      }
    },
    // Mesma regra do back (Appointments::Reschedule): paga e com mais de 24h.
    // Também controla o "Cancelar consulta": os dois aparecem juntos.
    canReschedule(appt) {
      return !!(appt.can_cancel && appt.order && appt.order.status === "paid");
    },
    openReschedule(appt) {
      this.rescheduleTarget = appt;
      this.$nextTick(() => this.$bvModal.show("reschedule-modal"));
    },
    async confirmReschedule({ slot_id, slot_date, hour }) {
      const appt = this.rescheduleTarget;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remarcar sua consulta para ${formatDate(
          slot_date,
          "dd MMM yyyy"
        )} às ${hour}?`,
        {
          title: "Remarcar consulta",
          okTitle: "Sim, remarcar",
          cancelTitle: "Voltar",
          okVariant: "cancel-confirm",
          cancelVariant: "cancel-outline",
          modalClass: "remarcar-consulta-modal"
        }
      );
      if (!confirmed) return;

      this.$bvModal.hide("reschedule-modal");
      this.reschedulingId = appt.id;
      try {
        await this.$store.dispatch("appointments/rescheduleAppointment", {
          appointmentId: appt.id,
          payload: { slot_id, scheduled_at: `${slot_date} ${hour}:00` }
        });
        await this.fetchTab("proximas");
        this.$bvToast.toast("Sua consulta foi remarcada.", {
          title: "Consulta remarcada",
          variant: "success",
          solid: true,
          autoHideDelay: 4000
        });
      } catch (error) {
        const message =
          error?.response?.data?.errors?.details ||
          "Não foi possível remarcar a consulta. Tente novamente.";
        this.$bvToast.toast(message, {
          title: "Erro",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000
        });
      } finally {
        this.reschedulingId = null;
      }
    }
  },

  computed: {
    ...mapGetters("appointments", [
      "futureAppointments",
      "pastAppointments",
      "canceledAppointments",
      "refundedAppointments",
      "appointmentsTotals"
    ]),

    tabs() {
      return [
        {
          key: "proximas",
          label: "Próximas",
          count: this.appointmentsTotals.future
        },
        {
          key: "realizadas",
          label: "Realizadas",
          count: this.appointmentsTotals.past
        },
        {
          key: "canceladas",
          label: "Canceladas",
          count: this.appointmentsTotals.canceled
        },
        {
          key: "reembolsadas",
          label: "Reembolsadas",
          count: this.appointmentsTotals.refunded
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 22px;
  width: auto;
}

#consultasView {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-bg-page);
}

// Layout e componentes usam as classes globais de _patient-shell.scss:
// .area, .area-head, .greet, .tabs, .tab, .tab--active,
// .appt-list, .appt-row, .appt-info, .appt-acts, .when,
// .state-box, .ill, .foot-mini, .vm-mark, .vm-wordmark

.appt-row {
  flex-wrap: wrap;
}

.appt-exams {
  flex-basis: 100%;
  min-width: 0; // descrição longa de exame não estica o card
  border-top: 1px solid var(--color-gray-100);
  padding-top: var(--space-3);
  margin-top: var(--space-1);
}

.appt-acts .badge--success {
  font-size: var(--font-size-sm);
}
</style>

<!--
  Estilo do modal de "Cancelar consulta" (aberto via $bvModal.msgBoxConfirm).
  Precisa ser global (não scoped): o modal é montado fora da árvore do
  componente, então o atributo de escopo do Vue não alcança seus elementos.
  Escopado pela classe única `cancel-consulta-modal` para não afetar outros
  modais/botões do app. Os modais de "Remarcar" (`remarcar-consulta-modal`)
  usam o mesmo visual.
-->
<style lang="scss">
.cancel-consulta-modal,
.remarcar-consulta-modal {
  .modal-footer {
    border-top: none;
  }

  .btn-cancel-outline {
    background: #fff;
    color: var(--color-primary);
    border: 1.5px solid var(--color-primary);

    &:hover {
      background: var(--color-primary-light);
    }
  }

  .btn-cancel-confirm {
    background: var(--color-accent);
    color: #fff;
    border: 1.5px solid var(--color-accent);

    &:hover {
      background: var(--color-accent-dark);
      border-color: var(--color-accent-dark);
    }
  }
}

// Grade de horários dentro do modal "Remarcar": fonte maior que a da página
// do médico, onde a TimeTable fica numa coluna estreita.
.remarcar-consulta-modal .tt {
  .tt-day-week {
    font-size: var(--font-size-xs);
  }

  .tt-day-date {
    font-size: var(--font-size-md);
  }

  .tt-slot {
    height: 40px;
    font-size: var(--font-size-sm);
  }

  .tt-more {
    font-size: var(--font-size-sm);
  }
}
</style>
